import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../utils/apiClient';
import { AppNotification } from '../types';
import { UNREAD_WATCHED, watchUnreadNotifications } from '../utils/notificationFeed';

// ── Types ────────────────────────────────────────────────────────────────────

interface UseNotificationsReturn {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  /** Fetch the latest page of notifications */
  refresh: () => Promise<void>;
  /** Load the next page (infinite scroll) */
  loadMore: () => Promise<void>;
  hasMore: boolean;
  /** Mark a single notification as read */
  markRead: (id: string) => Promise<void>;
  /** Mark all as read */
  markAllRead: () => Promise<void>;
  /** Delete a notification */
  deleteNotification: (id: string) => Promise<void>;
}

interface NotificationsPage {
  notifications: AppNotification[];
  total: number;
  page: number;
  limit: number;
}

/** Only without live updates (utils/notificationFeed.ts): how often the unread count is asked for */
const POLL_INTERVAL = 30_000; // 30 seconds
const PAGE_SIZE = 20;

// ── Hook ─────────────────────────────────────────────────────────────────────

interface UseNotificationsOptions {
  /** Notifications that arrived while the page was open (live updates only) */
  onArrive?: (notifications: AppNotification[]) => void;
}

export function useNotifications(userId: string | undefined, options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const onArriveRef = useRef(options.onArrive);
  onArriveRef.current = options.onArrive;
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const hasMore = (notifications?.length ?? 0) < total;

  // Fetch unread count (lightweight endpoint)
  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await apiClient.get<{ count?: number; unreadCount?: number }>('/notifications/unread');
      // Anything but the count (a page instead of the function) leaves the badge as it is
      const count = data && typeof data === 'object' ? data.unreadCount ?? data.count : undefined;
      if (typeof count === 'number') setUnreadCount(count);
    } catch {
      // Silent — unread badge is non-critical
    }
  }, [userId]);

  // Fetch a page of notifications
  const fetchNotifications = useCallback(async (pageNum: number, append = false) => {
    if (!userId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<NotificationsPage & { pagination?: { total?: number } }>(
        `/notifications?page=${pageNum}&limit=${PAGE_SIZE}`,
      );
      // Not the list (a page instead of the function): said, not shown as "no notifications"
      if (!data || typeof data !== 'object' || (data.notifications !== undefined && !Array.isArray(data.notifications))) {
        throw new Error('The notifications could not be read');
      }
      const items = data.notifications ?? [];
      setNotifications(prev => append ? [...prev, ...items] : items);
      setTotal(data.total ?? data.pagination?.total ?? 0);
      setPage(pageNum);
    } catch (err: any) {
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // The first page, then live updates: a new notification shows the moment it is written, with
  // no reload. Where they cannot be had (no cloud here, or refused) the unread count is polled.
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    fetchNotifications(1);
    fetchUnreadCount();

    const poll = () => {
      if (!pollTimerRef.current) pollTimerRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    };
    let stopLive: (() => void) | null = null;
    try {
      stopLive = watchUnreadNotifications(
        userId,
        ({ count, arrived, gone }) => {
          // At the watch's limit the exact number is the server's
          if (count >= UNREAD_WATCHED) void fetchUnreadCount();
          else setUnreadCount(count);
          if (arrived.length) {
            // The list as the server gives it, with the new ones at the top
            void fetchNotifications(1);
            onArriveRef.current?.(arrived);
          }
          if (gone.length) setNotifications(prev => prev.map(n => (gone.includes(n.id) ? { ...n, read: true } : n)));
        },
        () => { stopLive = null; poll(); },
      );
    } catch (error) {
      console.warn('[notifications] live updates unavailable:', error);
      stopLive = null;
    }
    if (!stopLive) poll();
    return () => {
      stopLive?.();
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [userId, fetchNotifications, fetchUnreadCount]);

  // Public API

  const refresh = useCallback(async () => {
    await Promise.all([fetchNotifications(1), fetchUnreadCount()]);
  }, [fetchNotifications, fetchUnreadCount]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;
    await fetchNotifications(page + 1, true);
  }, [hasMore, isLoading, page, fetchNotifications]);

  const markRead = useCallback(async (id: string) => {
    try {
      await apiClient.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silently fail — UI already updated optimistically
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await apiClient.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  }, []);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      await apiClient.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setTotal(prev => prev - 1);
    } catch {
      // Silently fail
    }
  }, []);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    refresh,
    loadMore,
    hasMore,
    markRead,
    markAllRead,
    deleteNotification,
  };
}
